import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { environment } from "../../environments/environments";
import "../UserProfileEdit/UserImageEdit.css";
import { getUserById, removeImage } from "../api/api";
import Swal from "sweetalert2";
import bgImage from "../../assets/images/brown-background.jpg";
import { jsxs } from "react/jsx-runtime";

const MAX_IMAGES = 5;

const UserImageEdit = () => {
  const [userdata, setUserdata] = useState(null);
  const [newImages, setNewImages] = useState([]);
  const [showUploadButton, setShowUploadButton] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const getUser = async () => {
      const storedUser = JSON.parse(localStorage.getItem("currentUser"));
      const _id = storedUser?.id || storedUser?._id;
      const res = await getUserById({ user_id: _id });

      setUserdata(res);
      setShowUploadButton(res?.data?.more_images?.length < MAX_IMAGES);
    };
    getUser();
  }, []);

  const handleSelectImages = (e) => {
    const files = Array.from(e.target.files);
    const currentImages = userdata?.data?.more_images || [];

    const remainingSlots = MAX_IMAGES - currentImages.length;
    if (files.length > remainingSlots) {
      alert(`You can only upload ${remainingSlots} more images.`);
      return;
    }

    // Add new image preview URLs for UI
    const newPreviews = files.map((file) => URL.createObjectURL(file));
    const updated = [...currentImages, ...newPreviews];

    setUserdata((prev) => ({
      ...prev,
      data: { ...prev.data, more_images: updated },
    }));

    // Store actual File objects for upload
    setNewImages((prev) => [...prev, ...files]);

    setShowUploadButton(updated.length < MAX_IMAGES);
  };

  const handleDeleteImage = async (index) => {
    const imageToDelete = userdata?.data?.more_images[index];

    const confirm = await Swal.fire({
      title: "Delete Image",
      text: "Are you sure you want to permanently delete this image?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });
    if (!confirm.isConfirmed) {
      return;
    }
    if (newImages.includes(imageToDelete)) {
      const filtered = newImages.filter((img) => img !== imageToDelete);
      setNewImages(filtered);
    } else {
      const storedUser = JSON.parse(localStorage.getItem("currentUser"));
      const _id = storedUser?.id || storedUser?._id;

      const payload = {
        user_id: _id,
        index: index.toString(),
        image_name: imageToDelete,
      };

      const response = await removeImage(payload);
      if (!response) {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to delete from server",
        });
        return;
      }

      Swal.fire({
        icon: "success",
        title: "Deleted",
        text: response.message,
        timer: 1500,
        showConfirmButton: false,
      });
    }

    const filteredImages = userdata.data.more_images.filter(
      (_, i) => i !== index
    );
    setUserdata((prev) => ({
      ...prev,
      data: { ...prev.data, more_images: filteredImages },
    }));
    setShowUploadButton(filteredImages.length < MAX_IMAGES);
  };

  const handleSubmit = async () => {
    if (!userdata?.data?.more_images?.length || 0) {
      await Swal.fire({
        icon: "warning",
        title: "No Images Selected",
        text: "Please upload at least one photo before submitting.",
        confirmButtonColor: "#3085d6",
      });
      return;
    }
    try {
      setIsLoading(true);
      const storedUser = JSON.parse(localStorage.getItem("currentUser"));
      const _id = storedUser?.id || storedUser?._id;

      const formData = new FormData();
      formData.append("user_id", _id);
      newImages.forEach((file, index) => {
        formData.append(`more_images${index}`, file);
      });

      const response = await axios.post(
        `${environment.baseURL}/updateuser`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      console.log("responsesss", JSON.stringify(response));
      if (response.data?.data?.approve_reject_new_image === "pending") {
        Swal.fire({
          icon: "success",
          title: "Success",
          text: response.data.message,
          confirmButtonColor: "#3085d6",
        }).then(() => navigate("/DocumentVerification"));
      } else if (response.data?.success) {
        Swal.fire({
          icon: "success",
          title: "Success",
          text: response.data.message,
          confirmButtonColor: "#3085d6",
        }).then(() => navigate("/Sidebar/EditProfile"));
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: response.data?.message || "Something went wrong",
          confirmButtonColor: "#d33",
        });
      }
    } catch (err) {
      console.error("Upload Error:", err);
      alert("Something went wrong!");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className="update-image-container"
      style={{
        backgroundImage: `url(${bgImage})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      <h3>Update Images</h3>
      <div className="scrollable-image-area">
        <div className="image-row">
          {(userdata?.data?.more_images || []).map((img, index) => (
            <div className="image-wrapper" key={index}>
              <img
                src={
                  typeof img === "string"
                    ? img.startsWith("blob:")
                      ? img
                      : `${environment.url}uploads/users/${img}`
                    : img.uri || ""
                }
                alt="preview"
                className="image-box"
              />

              <button
                className="delete-btn"
                onClick={() => handleDeleteImage(index)}
              >
                X
              </button>
            </div>
          ))}
        </div>

        {showUploadButton && (
          <div>
            <input
              type="file"
              multiple
              accept="image/*"
              onChange={handleSelectImages}
            />
          </div>
        )}

        <div className="btn-group">
          <button
            onClick={() => navigate("/Sidebar/EditProfile")}
            className="back-btn"
          >
            Back
          </button>
          <button onClick={handleSubmit} className="update-btn">
            {isLoading ? "Uploading..." : "Update"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UserImageEdit;
